import React, { useEffect, useState, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";

import Input from "shared/components/FormElements/Input";
import CustomSelect from "shared/components/FormElements/Select";
import DatePicker from "shared/components/FormElements/DatePicker";
import NotFound from "shared/components/UIElements/NotFound";
import DeleteModal from "components/shared/DeleteModal";
import ItemGenerated from "components/shared/ItemGenerated";
import FormFooter from "shared/components/FormElements/FormFooter";

import { VALIDATOR_REQUIRE } from "shared/util/validators";

import { useForm } from "shared/hooks/form-hook";
import { useHttpClient } from "shared/hooks/http-hook";
import { StoreContext } from "shared/context/store-context";

const ServiceTemplateItem = ({ itemId, onClose, isNewItem, gotoItem, DeleteItem, UpdateItem, InsertItem }) => {
  const { t } = useTranslation();
  const [isItemLoaded, setIsItemLoaded] = useState(false);
  const [loadedItem, setLoadedItem] = useState();
  const [generatedItemId, setGeneratedItemId] = useState();
  const { isLoading, error, sendRequest } = useHttpClient();
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { serviceTemplates, educationYearsOptions } = useContext(StoreContext);

  const itemType = "serviceTemplate";
  const endPointUpdate = "serviceTemplates";
  const endPointDelete = "serviceTemplates";
  const endPointCreate = "serviceTemplates";

  const [formState, inputHandler] = useForm(
    {
      year: { value: null, isValid: true },
      name: { value: "", isValid: true },
      serviceStartDate: { value: "", isValid: true },
      serviceEndingDate: { value: "", isValid: true },
      amount: { value: null, isValid: true },
    },
    true
  );

  // #region Standard API calls
  const fetchItem = useCallback(async () => {
    setIsItemLoaded(false);
    try {
      const currentServiceTemplate = serviceTemplates.find((serviceTemplate) => serviceTemplate.id === itemId);
      setLoadedItem(currentServiceTemplate);
    } catch (err) {}
    setIsItemLoaded(true);
  }, [serviceTemplates, itemId]);

  useEffect(() => {
    fetchItem();
  }, [itemId, fetchItem]);

  const itemUpdateSubmitHandler = async (event) => {
    event.preventDefault();

    const endpoint = isNewItem
      ? `${process.env.REACT_APP_BACKEND_URL}/${endPointCreate}`
      : `${process.env.REACT_APP_BACKEND_URL}/${endPointUpdate}/${itemId}`;

    const postType = isNewItem ? "POST" : "PATCH";

    const infoToBeSent = {
      year: formState.inputs.year.value,
      name: formState.inputs.name.value,
      serviceStartDate: formState.inputs.serviceStartDate.value,
      serviceEndingDate: formState.inputs.serviceEndingDate.value,
      amount: formState.inputs.amount.value,
    };

    try {
      const responseData = await sendRequest(endpoint, postType, JSON.stringify(infoToBeSent));

      if (responseData?.[itemType]) {
        if (isNewItem) {
          InsertItem(responseData[itemType]);
          setGeneratedItemId(responseData[itemType].id);
        } else {
          UpdateItem(responseData[itemType]);
          window.toast.success(t("updateSuccess"));
        }
      }
    } catch (err) {
      if (err?.message) {
        window.toast.error(err.message);
      } else {
        window.toast.error(t("actionFailed"));
      }
    }
  };

  //standard code
  if (isItemLoaded && !isNewItem && !loadedItem && !error) {
    return <NotFound item={itemType}></NotFound>;
  }

  if (generatedItemId) {
    return (
      <ItemGenerated
        itemType={itemType}
        onGotoNewItem={() => gotoItem(generatedItemId)}
        onCreateNewItem={() => setGeneratedItemId(null)}
        onClose={() => {
          setGeneratedItemId(false);
          onClose();
        }}
      />
    );
  }
  return (
    <React.Fragment>
      <DeleteModal
        endPoint={endPointDelete}
        itemType={itemType}
        itemId={itemId}
        onDeleted={() => {
          DeleteItem(itemId);
          onClose();
        }}
        onClose={() => setShowConfirmModal(false)}
        showModal={showConfirmModal}
      />

      {isItemLoaded && (
        <form className="form-container" onSubmit={itemUpdateSubmitHandler}>
          <CustomSelect
            options={educationYearsOptions}
            id="year"
            onInput={inputHandler}
            label="Eğitim-Öğretim Yılı"
            initialValid={true}
            initialValue={
              educationYearsOptions?.find((item) => item?.value.toString() === loadedItem?.year.toString()) || null
            }
          ></CustomSelect>

          <Input
            id="name"
            element="input"
            type="text"
            label="İsim"
            validators={[VALIDATOR_REQUIRE()]}
            errorText="İsim giriniz."
            initialValue={loadedItem?.name || ""}
            initialValid={isNewItem ? false : true}
            onInput={inputHandler}
          />

          <DatePicker
            id="serviceStartDate"
            onInput={inputHandler}
            label="Hizmet Başlangıç Tarihi"
            validators={[VALIDATOR_REQUIRE()]}
            initialValue={loadedItem?.serviceStartDate?.toString() || ""}
            initialValid={isNewItem ? false : true}
          ></DatePicker>

          <DatePicker
            id="serviceEndingDate"
            onInput={inputHandler}
            label="Hizmet Bitiş Tarihi"
            validators={[VALIDATOR_REQUIRE()]}
            initialValue={loadedItem?.serviceEndingDate?.toString() || ""}
            initialValid={isNewItem ? false : true}
          ></DatePicker>

          <Input
            id="amount"
            element="input"
            type="text"
            label="Toplam Ücret"
            validators={[VALIDATOR_REQUIRE()]}
            errorText="Hizmet bedelinin standart ücretini giriniz."
            initialValue={loadedItem?.amount || ""}
            initialValid={isNewItem ? false : true}
            onInput={inputHandler}
          />

          <FormFooter
            isNewItem={isNewItem}
            onUpdate={itemUpdateSubmitHandler}
            onDelete={() => setShowConfirmModal(true)}
            isUpdating={isLoading}
            disabled={!formState.isValid || isLoading}
          />
        </form>
      )}
    </React.Fragment>
  );
};

export default ServiceTemplateItem;
